// Shared client-side scoring helpers for the new question types.
// Mirrors the server-side helpers in server/index.js (kept deliberately
// simple - case/whitespace-insensitive substring presence checks).

/**
 * Count how many of the `keywords` appear in `text` (case-insensitive,
 * substring match). Returns the number of distinct keyword strings found.
 */
export function matchKeywordCount(text, keywords = []) {
  if (!text || !Array.isArray(keywords) || keywords.length === 0) return 0;
  const haystack = String(text).toLowerCase();
  return keywords.reduce((count, kw) => {
    const needle = String(kw || '').toLowerCase().trim();
    if (!needle) return count;
    return haystack.includes(needle) ? count + 1 : count;
  }, 0);
}

/**
 * Returns true if all keywords in the list are present in `text`
 * (case-insensitive substring match).
 */
export function matchesAllKeywords(text, keywords = []) {
  if (!Array.isArray(keywords) || keywords.length === 0) return true;
  return matchKeywordCount(text, keywords) === keywords.filter(Boolean).length;
}

/**
 * Compute precision / recall / F1 from sets of submitted vs expected items.
 * Both arrays should be arrays of strings (or anything with consistent toString).
 */
export function precisionRecall(submitted = [], expected = []) {
  const submittedSet = new Set(submitted.map((s) => String(s).toLowerCase().trim()));
  const expectedSet = new Set(expected.map((s) => String(s).toLowerCase().trim()));
  if (expectedSet.size === 0 && submittedSet.size === 0) {
    return { precision: 1, recall: 1, f1: 1, truePositives: 0, falsePositives: 0, falseNegatives: 0 };
  }
  let truePositives = 0;
  submittedSet.forEach((item) => {
    if (expectedSet.has(item)) truePositives += 1;
  });
  const falsePositives = submittedSet.size - truePositives;
  const falseNegatives = expectedSet.size - truePositives;
  const precision = submittedSet.size === 0 ? 0 : truePositives / submittedSet.size;
  const recall = expectedSet.size === 0 ? 0 : truePositives / expectedSet.size;
  const f1 = (precision + recall) === 0 ? 0 : (2 * precision * recall) / (precision + recall);
  return { precision, recall, f1, truePositives, falsePositives, falseNegatives };
}

/**
 * Score a free-text submission against a weighted keyword rubric.
 * Each rubric entry has `{ criterion, keywords, weight }`. A criterion is
 * met if at least one of its keywords appears in the text. Returns
 * `{ totalWeight, achievedWeight, percentage, perCriterion: [{ criterion, met }] }`.
 */
export function scoreRubric(text, rubric = []) {
  if (!Array.isArray(rubric) || rubric.length === 0) {
    return { totalWeight: 0, achievedWeight: 0, percentage: 0, perCriterion: [] };
  }
  const haystack = String(text || '').toLowerCase();
  const perCriterion = rubric.map((entry) => {
    const keywords = Array.isArray(entry?.keywords) ? entry.keywords : [];
    const weight = Number(entry?.weight ?? 1);
    const met = keywords.some((kw) => {
      const needle = String(kw || '').toLowerCase().trim();
      return needle && haystack.includes(needle);
    });
    return { criterion: String(entry?.criterion || ''), met, weight };
  });
  const totalWeight = perCriterion.reduce((sum, c) => sum + c.weight, 0);
  const achievedWeight = perCriterion.filter((c) => c.met).reduce((sum, c) => sum + c.weight, 0);
  const percentage = totalWeight === 0 ? 0 : Math.round((achievedWeight / totalWeight) * 100);
  return { totalWeight, achievedWeight, percentage, perCriterion };
}
